/**
 * This service is used to perform the actions on form, formControl, formGroup, formArray
 * Check the mentioned functions for more information
 * Remove this file if not needed
 */
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { Injectable } from "@angular/core";

@Injectable()
export class FormActionsService {
  constructor(private formBuilder: FormBuilder) {}

  /**
   * This function is used to return form array
   * @param formGroup {form group} - form group object with form controls
   */
  returnFormArray(formGroup): FormArray {
    return this.formBuilder.array([formGroup]);
  }

  /**
   * This function is used to return form group of controls
   * @param fromControls {object} - object of form controls
   */
  returnFormGroup(formControls): FormGroup {
    return this.formBuilder.group(formControls);
  }

  /**
   * Returns passed form controls
   * @param form {string} - variable/key of the form(member of this service)
   * Example, regForm = {
   *      name: ['', Validators.required],
   *      email: ['', Validators.required]
   * }
   * regForm should be the member of this service with respective form controls.
   * So that it can be accessed using this['regForm']
   */
  getFormControls(form) {
    return this.returnFormGroup(this[form]);
  }

  /**
   * This function is used to replace form control at an index in form array
   * @param formArray {FormArray} - form array in which the form control is being replaced
   * @param index {number} - array index where to push container
   * @param formControl {object} - form control object
   */
  replaceAt(formArray: FormArray, index, formControl) {
    formArray.setControl(index, formControl);
  }

  /**
   * This function is used to push a form control into a form array
   * @param formArray {FormArray} - form array in which the form control is being replaced
   * @param formControl {object} - form control object
   */
  addControl(formArray: FormArray, formControl) {
    formArray.push(formControl);
  }

  /**
   * This function is used to remove a control at an index from form array
   * @param formArray {FormArray} - form array in which the form control is being deleted
   * @param index {number} - index where form control is being deleted
   */
  removeControl(formArray: FormArray, index) {
    formArray.removeAt(index);
  }

  /**
   * This function is used to set validator
   * @param formControl {object} - form control object
   * @param validatorsArray {array} - array of validators
   */
  setValidators(formControl: FormControl, validatorsArray) {
    formControl.setValidators(validatorsArray);
    formControl.updateValueAndValidity();
  }

  /**
   * This function is used to clear validators
   * @param formControl {object} - form control object
   */
  clearValidators(formControl: FormControl, validatorsArray) {
    formControl.clearValidators();
    formControl.updateValueAndValidity();
  }

  /**
   * This function is used to fill form data in the ui
   * All form cotrols should be there in data which should follow same nesting structure as form
   * @param form {FormGroup} - form object which is having FormControl/FormArray/FormGroup
   * @param data {Object} - data to feed the form
   */
  fillFormInUI(form, data) {
    // if control is FormArray, loop through FormGroups inside it
    // if control is FormGroup, do recursion of this function
    // if control is FormControl, fill value
    Object.keys(form.controls).forEach(control => {
      if (form.controls[control].constructor === FormArray) {
        form.controls[control].controls.forEach((formGroup, index) => {
          this.fillFormInUI(formGroup, data[control][index]);
        });
      } else if (form.controls[control].constructor === FormGroup) {
        this.fillFormInUI(form.controls[control], data[control]);
      } else if (form.controls[control].constructor === FormControl) {
        form.get(control).setValue(data[control]);
      }
    });
  }

  /**
   * this function is used to check error for a given input control
   * @param control {FormControl} - form control to be validated
   * @param formSubmitted {boolean} - to validate the form on form submission
   */
  isInvalid(control, formSubmitted) {
    return (
      (control.invalid && (control.touched || control.dirty)) ||
      (formSubmitted && control.invalid)
    );
  }

  /**
   * this function is used to extract form group from given array of inputs
   * @param inputs {Array} - array of input fields
   */
  toReactiveForm(inputs: Array<{}>) {
    let group = {};
    inputs.forEach(input => {
      if (input["controlType"] === "formGroup") {
        group[input["key"]] = this.toReactiveForm(input["formInputs"]);
      } else if (input["controlType"] === "formArray") {
        let formArray = this.formBuilder.array([]);
        input["formInputs"].forEach(formGroup => {
          let fGroup = this.toReactiveForm(formGroup["formInputs"]);
          formArray.push(fGroup);
        });
        group[input["key"]] = formArray;
      } else if (input["controlType"] === 'submitButton') {
        //Don't add to form Group
      } else {
        group[input["key"]] = [input["value"] || "", input["validators"]];
      }
    });
    return this.formBuilder.group(group);
  }
}
